import { ColumnDef } from '@tanstack/react-table';
import { ReadAudience } from '@tinct/core-api-models';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@tinct/web-components';
import { CollectionColumnHeader } from '@tinct/web-collection';
import { IconInfoCircle } from '@tabler/icons-react';
import {cn} from "@tinct/core-utils";

export function useAudienceSelectionColumns(): ColumnDef<ReadAudience>[] {
  return [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Audience" />
      ),
      cell: ({ row }) => {
        const name = row.original.name ?? '-';
        const importStatus = row.original.lastImportJob?.status;
        return (
          <div className={cn(
            "font-semibold truncate max-w-[500px]",
            {
              'text-muted-foreground': importStatus === 'running',
            })}>{name}
            {importStatus === 'running' && " - Enrichment in progress..."}
          </div>
        );
      },
      meta: { className: 'w-full' },
    },
    {
      accessorKey: 'statistics.leadsCount',
      header: ({ column }) => (
        <div className="flex items-center gap-1">
          <CollectionColumnHeader column={column} title="Companies" />
          <Tooltip>
            <TooltipTrigger asChild>
              <IconInfoCircle className="size-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>Number of companies in this audience</TooltipContent>
          </Tooltip>
        </div>
      ),
      cell: ({ row }) => {
        const count = row.original.statistics?.leadsCount ?? 0;
        const importStatus = row.original.lastImportJob?.status;

        if(importStatus === 'running') {
          return "-";
        }
        return <div>{count}</div>;
      },
      size: 100,
      enableSorting: false,
    },
    {
      accessorKey: 'statistics.campaignsCount',
      header: ({ column }) => (
        <div className="flex items-center gap-1">
          <CollectionColumnHeader column={column} title="Campaigns" />
          <Tooltip>
            <TooltipTrigger asChild>
              <IconInfoCircle className="size-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>Number of campaigns using this audience</TooltipContent>
          </Tooltip>
        </div>
      ),
      cell: ({ row }) => {
        const count = row.original.statistics?.campaignsCount ?? 0;
        const importStatus = row.original.lastImportJob?.status;

        if(importStatus === 'running') {
          return "-";
        }
        return <div>{count}</div>;
      },
      size: 100,
      enableSorting: false,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Created" />
      ),
      cell: ({ row }) => {
        const createdAt = row.original.createdAt;
        if (!createdAt) return <div className="text-muted-foreground">-</div>;

        const date = new Date(createdAt);
        return (
          <div className="text-muted-foreground">
            {date.toLocaleDateString('en-GB')}
          </div>
        );
      },
      size: 110,
    },
  ];
}
